import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ProcessingDashboard.module.css';
import { Loader } from '@mantine/core';

interface ProcessingDashboardProps {
  status: 'uploading' | 'processing' | 'generating' | 'completed' | 'error';
  progress?: number;
  message?: string;
  error?: string;
  onComplete?: () => void;
}

const ProcessingDashboard: React.FC<ProcessingDashboardProps> = ({
  status = 'processing',
  progress = 0,
  message = '',
  error = '',
  onComplete,
}) => {
  const { t } = useTranslation('processingDashboard');
  const [dots, setDots] = useState('.');
  
  // Animating dots for loading state
  useEffect(() => {
    if (status !== 'completed' && status !== 'error') {
      const interval = setInterval(() => {
        setDots(prev => prev.length < 3 ? prev + '.' : '.');
      }, 500);
      
      return () => clearInterval(interval);
    }
  }, [status]);
  
  // Trigger onComplete when status changes to completed
  useEffect(() => {
    if (status === 'completed' && onComplete) {
      // Small delay to show completed state before calling onComplete
      const timer = setTimeout(() => {
        onComplete();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [status, onComplete]);
  
  const getStatusMessage = () => {
    switch (status) {
      case 'uploading':
        return `${t('processingDashboard.status.uploading')}${dots}`;
      case 'processing':
        return `${t('processingDashboard.status.processing')}${dots}`;
      case 'generating':
        return `${t('processingDashboard.status.generating')}${dots}`;
      case 'completed':
        return t('processingDashboard.status.completed');
      case 'error':
        return t('processingDashboard.status.error');
      default:
        return `${t('processingDashboard.status.default')}${dots}`;
    }
  };
  
  return (
    <div className={styles.dashboardContainer} data-testid="dashboard-component">
      <div className={styles.dashboardCard}>
        <div className={styles.headerSection}>
          <h2>{t('processingDashboard.header.title')}</h2>
        </div>
        
        <div className={styles.statusSection}>
          {status !== 'completed' && status !== 'error' ? (
            <Loader size="xl" color="#6366F1" className={styles.loader} />
          ) : status === 'completed' ? (
            <div className={styles.completedIcon}>✓</div>
          ) : (
            <div className={styles.errorIcon}>⚠</div>
          )}
          
          <p className={styles.statusMessage}>{message || getStatusMessage()}</p>
          
          {progress > 0 && progress < 100 && (
            <div className={styles.progressContainer}>
              <div 
                className={styles.progressBar} 
                style={{ width: `${progress}%` }}
              />
              <span className={styles.progressText}>{t('processingDashboard.progress.percentage', { progress })}</span>
            </div>
          )}
          
          {error && <p className={styles.errorMessage}>{error}</p>}
        </div>
        
        <div className={styles.infoSection}>
          <div className={styles.infoCard}>
            <h3>{t('processingDashboard.steps.title')}</h3>
            <ul>
              <li className={status === 'uploading' ? styles.activeStep : ''}>
                {t('processingDashboard.steps.uploadingContent')}
              </li>
              <li className={status === 'processing' ? styles.activeStep : ''}>
                {t('processingDashboard.steps.processingContent')}
              </li>
              <li className={status === 'generating' ? styles.activeStep : ''}>
                {t('processingDashboard.steps.generatingTitles')}
              </li>
              <li className={status === 'generating' ? styles.activeStep : ''}>
                {t('processingDashboard.steps.creatingDescription')}
              </li>
              <li className={status === 'generating' ? styles.activeStep : ''}>
                {t('processingDashboard.steps.findingHashtags')}
              </li>
              <li className={status === 'generating' ? styles.activeStep : ''}>
                {t('processingDashboard.steps.buildingTimeline')}
              </li>
              <li className={status === 'completed' ? styles.activeStep : ''}>
                {t('processingDashboard.steps.finalizingResults')}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessingDashboard;
